import { TESTDATA_MODAL } from '@enums/overlay-internal-events.enum';
import { TEST_DATA_SYMBOLS } from '@enums/test-data-symbols.enum';
import { ProjectSelection } from '@modules/project-selection';
import TestCaseService from '@modules/test-development/services/test-case.service';
import { RuntimeVariableService } from '@services/runtime-variable.service';
import { useTranslation, useInternalEvents } from '@testsigma/atoms';
import { RuntimeVariableValueSelector } from '@testsigma/organism';
import { useState, useCallback, useMemo } from 'react';

import {
  TestDataDrawerHelpInfoProps,
  TestDataDrawerLayout,
} from './test-data-drawer-layout';

import { useInfiniteQuery } from 'react-query';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';
import { CacheOperation } from '@enums/cache-operation.enum';

export const RuntimeVariableDrawer = ({
  appVersionId,
  isInsideRestApi = false,
  context,
}) => {
  const { t } = useTranslation();
  const [showProjectSwitcher, updateShowProjectSwitcher] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const {
    getCachingTime,
    cacheOperationHandler: runtimeCacheOperationHandler,
  } = useReactQueryCaching(CachingPrefix.FETCH_TESTDATA_RUNTIME, false);
  const { cacheOperationHandler: runtimeUsageCacheOperationHandler } =
    useReactQueryCaching(CachingPrefix.FETCH_RUNTIME_VARIABLE_USAGE, false);

  const publishInternalEvents = useInternalEvents([]);
  const onClose = useCallback(() => {
    // TODO: Need to remove manually only during step update of test step ~ Need some improvement there
    runtimeCacheOperationHandler(CacheOperation.REMOVE_CACHE);
    runtimeUsageCacheOperationHandler(CacheOperation.REMOVE_CACHE);
    publishInternalEvents(
      TESTDATA_MODAL.CLOSE_RUNTIME_VARIABLE,
      isInsideRestApi ? { isInsideRestApi: true } : null,
    );
  }, [publishInternalEvents]);
  const onDataClick = useCallback(
    (item) => {
      runtimeCacheOperationHandler(CacheOperation.REMOVE_CACHE);
      runtimeUsageCacheOperationHandler(CacheOperation.REMOVE_CACHE);
      publishInternalEvents(TESTDATA_MODAL.SELECTED_RUNTIME_VARIABLE, {
        ...item,
        context,
      });
    },
    [publishInternalEvents],
  );
  const [localAppVersionId, setLocalAppVersionId] = useState(appVersionId);

  const useGetRuntimeVariableUsage = (data, search) =>
    useInfiniteQuery(
      `${CachingPrefix.FETCH_RUNTIME_VARIABLE_USAGE}-${data.runTimeVariableName}-${localAppVersionId}-${search}`,
      async ({ pageParam = 0 }) => {
        let filterQuery = '';
        if (localAppVersionId) {
          filterQuery += `applicationVersionId:${localAppVersionId},afterTestParentId:null`;
        }
        filterQuery += `,runtimeVariableUsed:${data.runTimeVariableName}${
          search ? `,name:*${search}*` : ''
        }`;
        return await TestCaseService.findAll(filterQuery, 20, pageParam);
      },
      {
        getNextPageParam: (lastPage) =>
          lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1,
        staleTime: getCachingTime(),
        cacheTime: getCachingTime(),
      },
    );

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery(
    [
      `${CachingPrefix.FETCH_TESTDATA_RUNTIME}-${
        searchTerm || ''
      }-${localAppVersionId}`,
    ],
    async ({ pageParam = 0 }) => {
      const query = `applicationVersionId:${localAppVersionId}${
        searchTerm ? `,name:*${searchTerm}*` : ''
      }`;
      try {
        return await RuntimeVariableService.findAll(query, 20, pageParam);
      } catch (err) {
        throw err;
      }
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1,
      staleTime: getCachingTime(),
      cacheTime: getCachingTime(),
    },
  );

  const modifiedData = useMemo(() => {
    const receivedData =
      data?.pages?.flatMap((item) => item?.content).filter((item) => !!item) ||
      [];
    let processedData = [];
    for (let variable of receivedData) {
      processedData.push({
        runTimeVariableName: variable.name,
        usageCount: variable.usageCount,
      });
    }
    return processedData;
  }, [data]);

  const helpInfoProps: TestDataDrawerHelpInfoProps = {
    description: t('modal.help.runtime'),
    url: 'https://testsigma.com/docs/test-data/types/runtime/',
  };

  return (
    <TestDataDrawerLayout
      title={t('modal.title.runtime.variable')}
      symbol={TEST_DATA_SYMBOLS.RUNTIME_VARIABLE}
      onClose={onClose}
      helpInfo={helpInfoProps}
    >
      {showProjectSwitcher && (
        <div className='pb-4 px-5'>
          <div>
            <ProjectSelection
              applicationVersionId={localAppVersionId}
              onChangeApplicationVerisonId={setLocalAppVersionId}
              variant='runtime-test-data'
            />
          </div>
        </div>
      )}
      <div className='px-5 py-6 h-full'>
        <RuntimeVariableValueSelector
          onClick={onDataClick}
          runtimeVariables={modifiedData || []}
          onChangeSearch={setSearchTerm}
          isFetching={isFetching}
          onShowProjectcher={() => updateShowProjectSwitcher(true)}
          isShowingProjectSwitcher={showProjectSwitcher}
          useGetRuntimeVariableUsage={useGetRuntimeVariableUsage}
          hasMore={hasNextPage}
          handleLoadMore={fetchNextPage}
          searchTerm={searchTerm}
        />
      </div>
    </TestDataDrawerLayout>
  );
};
